import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Empty, Card } from 'antd';
import DetailBarDynamicCol from './DetailBarDynamicCol';
import SummaryChart from './charts/SummaryChart';
import SummaryRangeSelector from './SummaryRangeSelector';
import { isEmpty, cloneDeep } from 'lodash';
import Top from './top';
import moment from 'moment-timezone';
import { cleanFormatByDateReport } from '../../../../../Home/utils';
import { detailBarTotalCompute, processPercentage } from '../../../utils';
import { signUpsResponseData, groupByKeys } from '../../signups/components/utils';
import { orgLevelCustomization } from '../../../../../../../core/permissions/orgLevelCustomization';
import { computeSpend, getEffectiveCpm } from '../../../../../../../core/utils/campaigns';
import { useTimezone } from '../../../../../../../core/hooks/useTimezone';

// =============================================================================
// LOCKED tz contract — DO NOT CHANGE WITHOUT RE-VERIFYING vs develop deployed.
//
// Four anchors cooperate to give (a) UTC chart x-axis labels matching develop's
// deployed visual, (b) KPI parity with develop including previous-period delta,
// (c) chart and KPI bar agreeing on which UTC buckets are in scope:
//
//   1. cleanFormatByDateReport(_, 'UTC')      → row.xAxis is UTC day-string
//   2. defaultDateRange = moment.utc(xAxis)   → Custom pill matches chart
//   3. <SummaryRangeSelector timezone={effectiveTimezone}/>  (presets in user's tz)
//   4. Parent filter bridges picker bounds → UTC day boundaries via day-string
//      extraction: moment.utc(moment(picker).format('YYYY-MM-DD'),
//      'YYYY-MM-DD').startOf/endOf('day')
//
// Flipping any single anchor breaks one of (a)/(b)/(c). See verification harness
// in the team's notes and don't ship without re-running it.
// =============================================================================
const SummaryDetailBar = ({
  colStyle,
  chartData,
  bySummaryRangeDate,
  setBySummaryRangeDate,
  dataSummaryRangeDate,
  setDataSummaryRangeDate,
  showTransactions,
  showAttribution,
  hasEcommerceAttribution,
  signUpCampaign,
  isAdmin,
  orgConfig,
  campaignId,
  campaignType,
  attributionReportId,
  campaignOrder,
  exportData,
  checkStatus,
  hasInsights,
  chartsLoading,
  currentUser,
  setRangeType,
  rangeType,
  selectedCampaign,
  priceVisible,
  dataConfig,
  showSessions,
}) => {
  const { completeAttributionSummary, publishers, creatives, signUpsWithImpressions } = chartData;
  const { timezone: effectiveTimezone = 'UTC' } = useTimezone();
  const [summaryData, setSummaryData] = useState([]);
  const [summaryDataLoading, setSummaryDataLoading] = useState(true);
  const [signUpsData, setSignUpsData] = useState([]);
  const [totals, setTotals] = useState({
    impressions: 0,
    clicks: 0,
    walkIns: 0,
    observations: 0,
    transactions: 0,
    transactionTotalRevenue: 0,
    signUpsWithImpressions: 0,
    sessions: 0,
    spend: 0,
  });

  const [kpiPercentage, setKpiPercentage] = useState({
    percentages: {
      impressions: 0,
      clicks: 0,
      transactions: 0,
      walkIns: 0,
      observations: 0,
      revenue: 0,
      signUps: 0,
      roas: 0,
      spend: 0,
      sessions: 0,
    },
    previousValues: {
      impressions: 0,
      clicks: 0,
      transactions: 0,
      walkIns: 0,
      observations: 0,
      revenue: 0,
      signUps: 0,
      roas: 0,
      spend: 0,
      sessions: 0,
    },
    ranges: '',
  });

  const orgCustomization = orgLevelCustomization(dataConfig);
  const { currencyFormat, roasFormat } = orgCustomization;

  // Fallback date range for the Custom pill. UTC-anchored to match the chart's
  // UTC day-string xAxis labels — picker pill, presets, filter, and chart
  // labels all live in UTC so a UTC-midnight bucket renders on its UTC day
  // and is included by a picker window covering that UTC day.
  const defaultDateRange = useMemo(() => {
    if (!summaryData || summaryData.length === 0) return null;
    const sorted = summaryData
      .filter((d) => d && d.xAxis)
      .slice()
      .sort((a, b) => (a.date || 0) - (b.date || 0));
    if (sorted.length === 0) return null;
    const startStr = sorted[0].xAxis;
    const endStr = sorted[sorted.length - 1].xAxis;
    return [moment.utc(startStr), moment.utc(endStr)];
  }, [summaryData]);

  useEffect(() => {
    if (chartData && completeAttributionSummary) {
      setSummaryDataLoading(true);
      // UTC-pinned to match develop's chart x-axis labels — a UTC-midnight
      // bucket renders on its UTC calendar day regardless of the user's tz.
      setSummaryData(cleanFormatByDateReport(completeAttributionSummary, 'UTC'));
    }
    if (chartData && !isEmpty(signUpsWithImpressions)) {
      setSignUpsData(
        signUpsResponseData(
          groupByKeys(
            signUpsWithImpressions.map((signUp) => {
              return {
                ...signUp,
                dateChart: moment(moment(signUp.signUpDate ? signUp.signUpDate : signUp.signupTime).format('YYYY-MM-DD')).format(), // signUpDate is for V2 support
              };
            }),
            ['dateChart']
          )[0]
        ).sort((a, b) => {
          if (a.xAxis === b.xAxis) return 0;
          if (moment(a.xAxis).isAfter(b.xAxis)) return 1;
          return -1;
        })
      );
    }

    setSummaryDataLoading(false);
  }, [chartData, completeAttributionSummary, creatives, publishers, signUpsWithImpressions]);

  // Owns dataSummaryRangeDate computation so the chart and KPI window stay
  // tied to the picker selection. Hybrid: picker presets are computed in
  // effectiveTimezone (matches develop's KPI math via processPercentage),
  // chart x-axis labels are UTC day-strings (from cleanFormatByDateReport(_,
  // 'UTC')). Bridge the picker bounds to UTC day boundaries by extracting
  // the picker moment's local-tz day-string and re-parsing as UTC, so the
  // chart and the KPI bar agree on which UTC buckets are in scope without
  // edge-clipping.
  useEffect(() => {
    if (!summaryData || summaryData.length === 0) {
      setDataSummaryRangeDate([]);
      return;
    }
    if (!bySummaryRangeDate || bySummaryRangeDate.length !== 2 ||
        !bySummaryRangeDate[0] || !bySummaryRangeDate[1]) {
      setDataSummaryRangeDate(summaryData);
      return;
    }
    const startDate = moment.utc(
      moment(bySummaryRangeDate[0]).format('YYYY-MM-DD'),
      'YYYY-MM-DD'
    ).startOf('day');
    const endDate = moment.utc(
      moment(bySummaryRangeDate[1]).format('YYYY-MM-DD'),
      'YYYY-MM-DD'
    ).endOf('day');
    setDataSummaryRangeDate(
      summaryData.filter((row) => {
        const dateValue = moment.utc(row.xAxis, 'YYYY-MM-DD');
        return dateValue >= startDate && dateValue <= endDate;
      })
    );
  }, [summaryData, bySummaryRangeDate, setDataSummaryRangeDate]);

  useEffect(() => {
    setTotals(detailBarTotalCompute(dataSummaryRangeDate, bySummaryRangeDate, chartData));

    const current = chartData && chartData.completeAttributionSummary;

    const dates = [
      bySummaryRangeDate[0] || moment(!isEmpty(current) ? current[0].utcISODate : null),
      bySummaryRangeDate[1] || moment(!isEmpty(current) ? current.at(-1).utcISODate : null),
    ];

    setKpiPercentage(
      processPercentage(
        dates,
        cloneDeep(dataSummaryRangeDate),
        cloneDeep(current),
        selectedCampaign ? selectedCampaign.cpm : 0
      )
    );
  }, [bySummaryRangeDate, chartData, dataSummaryRangeDate, selectedCampaign]);

  return (
    <>
      {summaryDataLoading === true ? (
        <></>
      ) : isEmpty(summaryData) && !chartsLoading && !summaryDataLoading ? (
        <Card>
          <Empty
            description="Please refresh your browser to receive the latest updates."
            style={{ marginTop: 16 }}
          />
        </Card>
      ) : (
        <>
        <section className="campaign-summary__performance">
          {/* Unified section header — Performance label + page-level range selector.
              Both KPI bar and chart below consume the range from this selector. */}
          <header className="campaign-summary__performance-head">
            <div className="campaign-summary__performance-eyebrow mono-eyebrow">
              Performance
            </div>
            <SummaryRangeSelector
              rangeType={rangeType}
              bySummaryRangeDate={bySummaryRangeDate}
              setRangeType={setRangeType}
              setBySummaryRangeDate={setBySummaryRangeDate}
              timezone={effectiveTimezone}
              defaultDateRange={defaultDateRange}
            />
          </header>

          {/* KPI columns — flat inside the unified performance card */}
          <DetailBarDynamicCol
                showTransactions={showTransactions}
                showAttribution={showAttribution}
                hasEcommerceAttribution={hasEcommerceAttribution}
                colStyle={colStyle}
                signUpCampaign={signUpCampaign}
                clicks={totals.clicks}
                impressions={totals.impressions}
                totalWalkIns={totals.walkIns}
                transactionTotalRevenue={totals.transactionTotalRevenue}
                transactions={totals.transactions}
                signUpsData={signUpsData}
                signUps={totals.signUps}
                summaryData={summaryData}
                bySummaryRangeDate={bySummaryRangeDate}
                budget={campaignOrder.budget}
                selectedCampaign={selectedCampaign}
                priceVisible={priceVisible}
                kpiPercentage={kpiPercentage}
                dataConfig={dataConfig}
                orgCurrencyFormat={currencyFormat}
                orgRoasFormat={roasFormat}
                spend={
                  campaignOrder.pricingModel === 'CPM' || !campaignOrder.pricingModel
                    ? computeSpend(totals.impressions, getEffectiveCpm(campaignOrder))
                    : totals.spend
                }
                sessions={totals.sessions}
                showSessions={showSessions}
              />

          {/* Chart — directly inside the performance card, edge-to-edge */}
          {!isEmpty(summaryData) && (
            <SummaryChart
              byDates={isEmpty(summaryData) ? [] : summaryData}
              showTransactions={showTransactions}
              showAttribution={showAttribution}
              bySummaryRangeDate={bySummaryRangeDate}
              setBySummaryRangeDate={setBySummaryRangeDate}
              dataSummaryRangeDate={dataSummaryRangeDate}
              setDataSummaryRangeDate={setDataSummaryRangeDate}
              reportData={summaryData}
              signUpsData={signUpsData}
              chartsBuilt={chartData.chartsBuilt}
              setRangeType={setRangeType}
              rangeType={rangeType}
              currencyFormat={currencyFormat}
              showSessions={showSessions}
            />
          )}

          {/* Top Publishers / Top Creatives — inside the performance card now.
              SCSS strips the inner AntD Card chrome so they read as sections
              of this card, not floating sub-cards. */}
          <div className="campaign-summary__tops">
            <Row gutter={[18, 18]}>
              {campaignOrder.aggregateData ? (
                <>
                  {campaignOrder.aggregateData.topPublishers &&
                    campaignOrder.aggregateData.topPublishers.length > 0 && (
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <Top
                          imageIndex="imageUrl"
                          title="Publishers"
                          currentUser={currentUser}
                          data={
                            campaignOrder.aggregateData.topPublishers.filter(
                              (i) => i.xAxis !== '[tail aggregate]'
                            ) || []
                          }
                        />
                      </Col>
                    )}
                  {campaignOrder.aggregateData.topCreatives &&
                    campaignOrder.aggregateData.topCreatives.length > 0 && (
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <Top
                          imageIndex="creativeUrl"
                          title="Creatives"
                          currentUser={currentUser}
                          data={campaignOrder.aggregateData.topCreatives || []}
                        />
                      </Col>
                    )}
                </>
              ) : (
                <>
                  <Col xs={12}>
                    <Top imageIndex="imageUrl" title="Publishers" data={[]} />
                  </Col>
                  <Col xs={12}>
                    <Top imageIndex="creativeUrl" title="Creatives" data={[]} />
                  </Col>
                </>
              )}
            </Row>
          </div>
        </section>
        </>
      )}
    </>
  );
};

export default SummaryDetailBar;
